import React, { useEffect, useState } from 'react';
import { fetchApi } from '@api/fetchApi';
import { IDynamicDropdownParamsType, ITableFilterFieldAttributes } from '@components-shared/table/TableFilter';
import { handleError } from '@utils/errors';
import { cacheService } from '@utils/library';
import { EmptyObject } from '../../shared.interfaces';
import { Select } from '../Select';
import { Option } from '../Select/types';
import { ApiCommonType } from '../formField.interface';

const CACHE_PREFIX = '__dynamicDropdown';

interface IDynamicDropDownProps {
  api: ApiCommonType;
  attributes?: ITableFilterFieldAttributes | EmptyObject;
  params?: IDynamicDropdownParamsType;
  options?: Option[];
  name: string;
  value?: string;
  onChange: (name: string, value: string) => void;
  valid?: boolean;
  validationMessage?: string;
  useCache?: boolean;
  tooltip?: string;
}

type DynamicDropDownDataType = (
  | {
      id?: number;
      name?: string;
      value?: string | number;
      localizationKey?: string;
    }
  | string
)[];

export const DynamicDropdown = (props: IDynamicDropDownProps) => {
  const [disabled, setDisabled] = useState(true);
  const [options, setOptions] = useState<Option[]>([]);

  useEffect(() => {
    const { api, attributes = {}, name, useCache } = props;
    const { disabled } = attributes;

    const apiString = api && typeof api === 'function' ? getApiFromFunction() : api;

    if (apiString) {
      const cacheOptions = cacheService.getCachedValue<Option[]>(name, CACHE_PREFIX, apiString);

      if (cacheOptions && useCache) {
        setOptions(cacheOptions);
        setDisabled(false);
        return;
      } else {
        void getOptions(apiString, disabled);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getApiFromFunction = () => {
    const { params = {}, api } = props;
    const { parentContext = {} } = params;

    return parentContext.props && typeof api === 'function' ? api(parentContext.props) : '';
  };

  const getOptions = async (api: string, disabled?: boolean): Promise<void> => {
    const { name, useCache } = props;

    try {
      const response = await fetchApi.get<DynamicDropDownDataType>(api);

      const options = response.data.map((item) => {
        if (typeof item === 'object') {
          let value;

          if (typeof item.value === 'object') {
            value = JSON.stringify(item.value);
          } else if (item.id !== undefined) {
            value = item.id;
          } else {
            // eslint-disable-next-line prefer-destructuring
            value = item.value;
          }

          return { value, name: item.localizationKey || item.name || item.id };
        } else {
          return { value: item };
        }
      });

      useCache && cacheService.cacheValue(name, options, CACHE_PREFIX, api);

      setOptions(options);
      setDisabled(!!disabled);
    } catch (error) {
      handleError('Failed to fetch options', error);
    }
  };

  const { attributes: propsAttributes, options: propsOptions = [] } = props;

  const attributes = { ...propsAttributes, disabled };
  const allOptions = [...options, ...propsOptions];
  const uniqOptions: Option[] = [...new Set(allOptions.map((o) => JSON.stringify(o)))].map((s) => JSON.parse(s));

  return <Select {...props} attributes={attributes} options={uniqOptions} testID="formDynamicDropdown" />;
};
